/**
 * Evaluation Wizard
 *
 * Main wizard component for manager evaluation workflow.
 * 6-step process: Context -> Tasks -> VakCompetencies -> BasisCompetencies -> Review -> Agreements
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../../../components/Layout';
import { WizardProvider, useWizard } from './WizardContext';
import { WizardStepper } from './WizardStepper';
import { Step1Context } from './Step1Context';
import { Step2Tasks } from './Step2Tasks';
import { StepKerntaken } from './StepKerntaken';
import { Step3Competencies } from './Step3Competencies';
import { Step4BasisCompetencies } from './Step4BasisCompetencies';
import { Step5Review } from './Step5Review';
import { Step6Agreements } from './Step6Agreements';
import { useEvaluation, useDeleteEvaluation } from '../useEvaluations';
import { useAuth } from '../../auth';
import type { EvaluationStatus } from '../../../lib/evaluationsApi';

// Status badge helper component
function StatusBadge({ status }: { status?: EvaluationStatus }) {
  const statusConfig: Record<EvaluationStatus, { label: string; bgClass: string; textClass: string }> = {
    draft: { label: 'Concept', bgClass: 'bg-yellow-100', textClass: 'text-yellow-700' },
    submitted: { label: 'Ingediend', bgClass: 'bg-blue-100', textClass: 'text-blue-700' },
    in_review: { label: 'In Review', bgClass: 'bg-purple-100', textClass: 'text-purple-700' },
    final: { label: 'Definitief', bgClass: 'bg-green-100', textClass: 'text-green-700' },
    cancelled: { label: 'Geannuleerd', bgClass: 'bg-gray-100', textClass: 'text-gray-700' },
  };

  const config = status ? statusConfig[status] : statusConfig.draft;

  return (
    <span className={`px-3 py-1 text-xs font-medium ${config.bgClass} ${config.textClass} rounded-full`}>
      {config.label}
    </span>
  );
}

function WizardContent({ evaluationId }: { evaluationId: number }) {
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const {
    currentStep,
    isLoading,
    isSaving,
    isDirty,
    error,
    goToStep,
    canNavigateToStep,
    isStepComplete,
  } = useWizard();
  const { data: evaluation } = useEvaluation(evaluationId);
  const deleteEvaluation = useDeleteEvaluation();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Flow is not completed if status is not 'final'
  const canEdit = evaluation?.status !== 'final';
  // Show edit button only on step 5 or 6 (Review & Agreements)
  const showEditButton = canEdit && currentStep >= 5;
  // HR/admin can delete any non-final evaluation
  // Managers can delete their own team's non-final evaluations
  const isOwnTeamEvaluation = evaluation?.managerId === user?.id;
  const canDelete = canEdit && (hasRole(['hr', 'admin']) || (hasRole(['beoordelaar']) && isOwnTeamEvaluation));

  const handleFinalize = () => {
    navigate('/team');
  };

  const handleDelete = async () => {
    try {
      await deleteEvaluation.mutateAsync(evaluationId);
      navigate('/team');
    } catch {
      // Error handled by mutation
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Wizard laden...</span>
      </div>
    );
  }

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1Context evaluationId={evaluationId} />;
      case 2:
        // Epic 4 (Story 4.2, AC #2, AD-4): wizard dispatcht op scoring_model.
        // tasks_v1-beoordelingen die al open staan blijven exact Step2Tasks
        // gebruiken (geen wijziging aan Step2Tasks.tsx zelf); StepKerntaken
        // bedient uitsluitend core_tasks_v2.
        return evaluation?.scoringModel === 'core_tasks_v2'
          ? <StepKerntaken evaluationId={evaluationId} />
          : <Step2Tasks evaluationId={evaluationId} />;
      case 3:
        return <Step3Competencies evaluationId={evaluationId} />;
      case 4:
        return <Step4BasisCompetencies evaluationId={evaluationId} />;
      case 5:
        return <Step5Review evaluationId={evaluationId} />;
      case 6:
        return <Step6Agreements evaluationId={evaluationId} onFinalize={handleFinalize} />;
      default:
        return <Step1Context evaluationId={evaluationId} />;
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header with status */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate(-1)}
            className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Terug
          </button>
          <h1 className="text-2xl font-bold text-gray-900 mt-2">Beoordelingswizard</h1>
        </div>
        <div className="flex items-center gap-3">
          {isDirty && (
            <span className="text-xs text-yellow-600 flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
              Niet opgeslagen
            </span>
          )}
          {isSaving && (
            <span className="text-xs text-blue-600 flex items-center gap-1">
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
              Opslaan...
            </span>
          )}
          {/* Edit button - only shown on step 5/6 when not finalized */}
          {showEditButton && (
            <button
              onClick={() => goToStep(2)}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Beoordeling bewerken"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          )}
          {/* Delete button - only shown for HR/admin when not finalized */}
          {canDelete && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Beoordeling verwijderen"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
          {/* Print buttons */}
          <button
            onClick={() => window.open(`/beoordeling/${evaluationId}/print`, '_blank')}
            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Scoringsformulier afdrukken"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
          </button>
          <button
            onClick={() => window.open(`/beoordeling/${evaluationId}/rapport`, '_blank')}
            className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
            title="Beoordelingsverslag genereren (AI)"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </button>
          <StatusBadge status={evaluation?.status} />
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Stepper */}
      <div className="mb-8 pt-4">
        <WizardStepper
          currentStep={currentStep}
          onStepClick={goToStep}
          canNavigateToStep={canNavigateToStep}
          isStepComplete={isStepComplete}
          scoringModel={evaluation?.scoringModel}
        />
      </div>

      {/* Step content */}
      <div className="bg-gray-50 rounded-lg p-6">
        {renderStep()}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900">Beoordeling verwijderen</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Weet je zeker dat je deze beoordeling wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Annuleren
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteEvaluation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {deleteEvaluation.isPending ? 'Verwijderen...' : 'Verwijderen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function EvaluationWizard() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const evaluationId = parseInt(id || '', 10);

  if (!evaluationId || isNaN(evaluationId)) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-700">Ongeldige beoordeling ID.</p>
            <button
              onClick={() => navigate('/team')}
              className="mt-4 text-blue-600 hover:text-blue-800"
            >
              Terug naar team overzicht
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <WizardProvider evaluationId={evaluationId}>
        <WizardContent evaluationId={evaluationId} />
      </WizardProvider>
    </Layout>
  );
}
